/**
 * Custom error types for Firebase authentication flows.
 */

export const PROVIDER_LABELS: Record<string, string> = {
  'google.com': 'Google',
  'github.com': 'GitHub',
  password: 'email and password',
};

/**
 * Thrown when the user successfully authenticated with their primary provider
 * but the secondary credential (stored as pending) failed to link.
 *
 * The user IS signed in — this error only indicates the linking step failed.
 * Login.tsx should hold the redirect and show actionable recovery steps.
 */
export class AccountLinkFailedError extends Error {
  constructor(public readonly pendingProvider: string) {
    super(`ACCOUNT_LINK_FAILED:${pendingProvider}`);
    this.name = 'AccountLinkFailedError';
  }

  get pendingProviderLabel(): string {
    return PROVIDER_LABELS[this.pendingProvider] ?? this.pendingProvider;
  }
}

/**
 * Thrown when a sign-in attempt finds an existing account with the same email
 * registered under a different provider.
 *
 * The pending OAuth credential is held in memory (AuthContext) and will be
 * linked automatically once the user authenticates with their original provider.
 */
export class AccountLinkRequiredError extends Error {
  constructor(
    public readonly email: string,
    public readonly pendingProvider: string,
  ) {
    super(`ACCOUNT_LINK_REQUIRED:${pendingProvider}:${email}`);
    this.name = 'AccountLinkRequiredError';
  }

  /** Human-readable label for the provider the user was trying to add. */
  get pendingProviderLabel(): string {
    return PROVIDER_LABELS[this.pendingProvider] ?? this.pendingProvider;
  }

  /**
   * Suggested alternative sign-in methods — everything except the pending
   * provider, since that's what triggered the conflict.
   */
  get suggestedProviderLabels(): string[] {
    return Object.entries(PROVIDER_LABELS)
      .filter(([id]) => id !== this.pendingProvider)
      .map(([, label]) => label);
  }
}

/**
 * Thrown when a sign-in or sign-up attempt succeeds but the user's email
 * address has not yet been verified.
 *
 * A verification email is sent automatically before this error is thrown.
 * Login.tsx should show a "check your inbox" banner with a resend button.
 * The user is signed out before the error is thrown — they have no active
 * session and cannot access the API until they click the verification link.
 */
export class EmailNotVerifiedError extends Error {
  constructor(public readonly email: string) {
    super(`EMAIL_NOT_VERIFIED:${email}`);
    this.name = 'EmailNotVerifiedError';
  }
}

/**
 * Thrown when a provider account is already linked to a *different* Firebase user.
 *
 * Surfaced when linkWithPopup() returns auth/credential-already-in-use.
 * The caller (SignInMethods) should show a non-generic inline error so the user
 * understands why they cannot link — it is NOT safe to silently succeed here.
 */
export class ProviderLinkConflictError extends Error {
  constructor(public readonly provider: string) {
    super(`PROVIDER_LINK_CONFLICT:${provider}`);
    this.name = 'ProviderLinkConflictError';
  }

  get providerLabel(): string {
    return PROVIDER_LABELS[this.provider] ?? this.provider;
  }
}

/**
 * Thrown when Firebase requires the user to re-authenticate before a sensitive
 * operation (auth/requires-recent-login).
 *
 * Callers should open ReauthModal and retry the operation on onConfirmed().
 */
export class ReauthRequiredError extends Error {
  constructor() {
    super('REAUTH_REQUIRED');
    this.name = 'ReauthRequiredError';
  }
}
